import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
import { microsoftConsumerSecurityClient } from 'client-factories/MicrosoftConsumerSecurityClientFactoryV1';
import { RecommendationAction, RecommendationActionType } from 'mcsclient/models';
import { ApiCallResultStatus } from 'store/apiCallResultStatus';
import { createAsyncApiThunk, createOneOffAsyncApiThunk } from 'store/reduxHelpers';

export interface IRecommendationAction {
  type: RecommendationActionType;
  buttonText: string;
}

export interface IRecommendation {
  id: string;
  type: string;
  title: string;
  subtitle: string;
  webViewUrl: string;
  actions: IRecommendationAction[];
}

export interface IRecommendationsState {
  allRecommendations: IRecommendation[];
  fetchApiStatus: ApiCallResultStatus;
  fetchApiError?: string;
}

export function getDefaultRecommendationsState(): IRecommendationsState {
  return {
    allRecommendations: [],
    fetchApiStatus: ApiCallResultStatus.DEFAULT,
  };
}

export const getAllRecommendationsAsync = createOneOffAsyncApiThunk(
  'recommendations/getAllRecommendationsAsync',
  () => microsoftConsumerSecurityClient.recommendations.getRecommendations(),
  ({ recommendations }) => recommendations.fetchApiStatus
);

const handleGetAllRecommendationsAsync = (builder: ActionReducerMapBuilder<IRecommendationsState>): void => {
  builder
    .addCase(getAllRecommendationsAsync.pending, (state) => {
      state.fetchApiStatus = ApiCallResultStatus.LOADING;
    })
    .addCase(getAllRecommendationsAsync.fulfilled, (state, { payload }) => {
      const recommendations = payload;
      state.allRecommendations = recommendations.filter((reco): reco is IRecommendation =>
        Object.entries(reco).every(([key, value]) => {
          if (key === 'actions') {
            return value.every((action: RecommendationAction) => action.buttonText !== undefined && action.type !== undefined);
          }
          return value !== undefined;
        })
      );
      state.fetchApiStatus = ApiCallResultStatus.SUCCESS;
    })
    .addCase(getAllRecommendationsAsync.rejected, (state, { error }) => {
      state.fetchApiStatus = ApiCallResultStatus.FAILED;
      state.fetchApiError = error.message;
    });
};

const initialState: IRecommendationsState = getDefaultRecommendationsState();

export const dismissRecommendationAsync = createAsyncApiThunk(
  'recommendations/dismissRecommendationAsync',
  ({ recommendationId }: { recommendationId: string }) =>
    microsoftConsumerSecurityClient.recommendations.dismissRecommendation(recommendationId, { body: { isDismissed: true } })
);

const handleDismissRecommendationAsync = (builder: ActionReducerMapBuilder<IRecommendationsState>): void => {
  builder
    .addCase(dismissRecommendationAsync.pending, (state) => {
      state.fetchApiStatus = ApiCallResultStatus.LOADING;
    })
    .addCase(dismissRecommendationAsync.fulfilled, (state, { payload }) => {
      const { id } = payload;
      const index = state.allRecommendations.findIndex((r) => r.id === id);
      if (index !== -1) {
        state.allRecommendations.splice(index, 1);
      }
      state.fetchApiStatus = ApiCallResultStatus.SUCCESS;
    })
    .addCase(dismissRecommendationAsync.rejected, (state, { error }) => {
      state.fetchApiStatus = ApiCallResultStatus.FAILED;
      state.fetchApiError = error.message;
    });
};

export const recommendationsSlice = createSlice({
  name: 'recommendations',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    handleGetAllRecommendationsAsync(builder);
    handleDismissRecommendationAsync(builder);
  },
});

export default recommendationsSlice.reducer;
